import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  FormControl
} from '@angular/forms';
import { Component, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, throwError, of } from 'rxjs';
import { switchMap, tap, catchError } from 'rxjs/operators';
import { DataTableHeaderOption, ErrorControl, RadioSelectComponent, createErrorControlMap } from '@dxo/ng-swift-ui';
import { AccountType, AccountTypeUI } from '@benefitsui/app/api/benefits-enums';
import {  
  InvoiceDetails,
  LineItem,
  PaymentClient,
  PaymentRequest,
  SearchSortDirection,
  SearchSortField
} from '@benefitsui/app/api/benefits-clients';
import { BillingService } from '@benefitsui/app/pages/billing-page/billing.service';
import { Constants, PaymentData, PaymentSearchParameters } from '@benefitsui/app/pages/billing-page/billing-page.types';
import { BillingPageComponent } from '@benefitsui/app/pages/billing-page/billing-page.component';
import { UserContextService } from '@benefitsui/app/services/user-context/user-context.service';
import { UserContext } from '@benefitsui/app/services/user-context/user-context.types';
import { ModalData, ModalType, PaymentInformation } from './payment.types';
import { PolicyContext } from '@benefitsui/app/services/policy-context/policy-context.types';
import { PolicyContextService } from '@benefitsui/app/services/policy-context/policy-context.service';
import { HttpErrorService } from '@benefitsui/app/services/http-error/http-error.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Defaults } from '@benefitsui/app/shared/defaults/defaults';
import { currencyParse, currencyStringCreate } from '@benefitsui/app/shared/utilities/currency-utilities';

// :  Constants
const AccountFieldLengthMax = 17;
const AccountFieldLengthMin = 5;
const RoutingFieldLength = 9;

// :  Computed constants
const ErrorMessageAccount = `Entry must be ${AccountFieldLengthMin} to ${AccountFieldLengthMax} digits`;
const ErrorMessageRouting = `Entry must be ${RoutingFieldLength} digits.`;

const getErrorMap = (formGroup: UntypedFormGroup) =>
  createErrorControlMap(formGroup, {
    accountNumber: {
      required: ErrorMessageAccount,
      maxlength: `Entry must not exceed ${AccountFieldLengthMax} characters`,
      minlength: `Entry must be at least ${AccountFieldLengthMin} characters`
    },
    routingNumber: {
      required: ErrorMessageRouting,
      maxlength: ErrorMessageRouting,
      minlength: ErrorMessageRouting
    },
    accountTypeSelection: {
      required: 'Please select account'
    }
  }) as object as {
    accountNumber: ErrorControl;
    routingNumber: ErrorControl;
    accountTypeSelection: ErrorControl;
  };

@Component({
  selector: 'app-billing-payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.scss'],
  standalone: false,
  providers: [PolicyContextService]
})
export class BillingPaymentComponent implements OnInit {
  // :  Constants
  AccountKey = 'account';
  AccountNumberKey = 'accountNumber';
  AccountNumberText = 'Account number';
  AccountTypeName = 'account-type';
  AccountTypeSelectionKey = 'accountTypeSelection';
  AccountTypeText = 'Account type';
  AmountKey = Constants.AmountText;
  AmountText = Constants.AmountText;
  AmountUiKey = 'AmountUI';
  BillGroupKey = Constants.BillGroupKey;
  BillGroupText = Constants.BillGroupText;
  BillGroupNameKey = 'BillGroupName';
  ButtonTextPayNow = 'Pay now';
  CharSetNumericWithDecimal = /[0-9.]/;
  CharSetNumericWithDecimalDollarComma = /[0-9.$,]/;
  CloseText = Constants.CloseText;
  ErrorMessageAccountLocal = ErrorMessageAccount;
  ErrorMessageCurrencyNotFloat = 'Must be a valid amount';
  InvoicePeriodAndNumberText = Constants.InvoicePeriodAndNumberText;
  InvoiceKey = Constants.InvoiceText;
  InvoiceText = Constants.InvoiceText;
  LegalType = ModalType.Legal;
  ModalBodyEmail = 'An email confirmation is on the way.';
  ModalBodyError4xx = 'Please ensure all details are entered correctly and try again.';
  ModalBodyError5xx =
    'Please attempt the transaction again after some time. Rest assured, our IT team has been informed and is looking into it.';
  ModalBodyLegal =
    'I authorize my payment to be processed as an electronic funds transfer or draft drawn from my' +
    ' account. If the payment is returned unpaid, I authorize you or your service provider to collect the payment' +
    " and my state's return item fee by electronic funds transfer(s) or draft(s) drawn from my account. If this" +
    ' payment is from a corporate account, I make these authorizations as an authorized corporate representative' +
    ' and agree that the NACHA operating rules will bind the entity.';
  ModalBodyPermission = 'You are not allowed to pay invoices.';
  ModalBodySuccess = 'Thank you for your payment.  Processing the transaction may take two business days to complete.';
  ModalPrimary = false;
  ModalShowClose = false;
  ModalTitleError4xx = 'We encountered a snag with processing your payment';
  ModalTitleError5xx = 'There is an issue with your payment';
  ModalTitleLegal = 'Electronic check terms & conditions';
  ModalTitlePermission = "Oops, you don't have access";
  ModalTitleSuccess = 'We received your payment';
  ModalWidth = 482;
  PaymentAmountText = 'Payment amount';
  PaymentCardHeader = 'Selected invoice(s) to pay';
  RoutingKey = 'routing';
  RoutingNumberKey = 'routingNumber';
  RoutingNumberText = 'Routing number';
  SortDirectionLocal = SearchSortDirection;
  SortKey = Constants.SortKey;
  TotalAmountText = 'Total amount';
  
  // :  Computed constants
  LegalPrefaceText = `By clicking "${this.ButtonTextPayNow}", I agree to the `;

  // :  Constructor
  constructor(
    private billingService: BillingService,
    private errorService: HttpErrorService,
    private formBuilder: UntypedFormBuilder,
    private parent: BillingPageComponent,
    private paymentClient: PaymentClient,
    private policyContextService: PolicyContextService,
    private userContextService: UserContextService
  ) {
    this.formGroup = this.formBuilder.group({
      accountNumber: new FormControl<string>(Constants.StringEmpty, {
        validators: [
          Validators.required,
          Validators.minLength(AccountFieldLengthMin),
          Validators.maxLength(AccountFieldLengthMax),
          Validators.pattern('[0-9]{5,17}')
        ],
        updateOn: Defaults.blurString
      }),
      routingNumber: new FormControl<string>(Constants.StringEmpty, {
        validators: [
          Validators.required,
          Validators.maxLength(RoutingFieldLength),
          Validators.minLength(RoutingFieldLength),
          Validators.pattern('[0-9]{9,9}')
        ],
        updateOn: Defaults.blurString
      }),
      accountTypeSelection: [Constants.StringEmpty, [Validators.required]]
    });

    this.formErrors = {
      ...getErrorMap(this.formGroup)
    };

    this.paymentDataArray = this.billingService.getPaymentTableCells();
    this.policyContext = this.policyContextService.get();
    this.totalAmount = this.totalAmountCalculate(this.paymentDataArray);
    this.userContext = this.userContextService.get();
  }

  // :  Data members
  private hasSubmitPermission = false;
  private page$ = new BehaviorSubject<number>(1);
  private pageSize$ = new BehaviorSubject<number>(50);
  private paymentDataArray: PaymentData[] = [];
  private policyContext: PolicyContext;
  private sortDirection$ = new BehaviorSubject<SearchSortDirection>(SearchSortDirection.Descending);
  private sortField$ = new BehaviorSubject<SearchSortField>(SearchSortField.Invoice);
  private userContext: UserContext;

  accountTypeOptions: RadioSelectComponent['data'] = [
    [AccountType.Checking, AccountTypeUI.Checking],
    [AccountType.Savings, AccountTypeUI.Savings]
  ].map(([value, label]) => ({ name: this.AccountTypeName, value, label, id: value }));

  formErrors: { accountNumber: ErrorControl; routingNumber: ErrorControl; accountTypeSelection: ErrorControl };
  formGroup: UntypedFormGroup;

  hasSubText = false;
  isLoading = false;

  modalData: ModalData = {
    title: Constants.StringEmpty,
    body: Constants.StringEmpty,
    subText: Constants.StringEmpty,
    type: ModalType.Success
  };

  modalShow = false;

  searchParameter: PaymentSearchParameters = {
    page: 1,
    pageSize: 10,
    sortField: SearchSortField.Invoice,
    sortDirection: SearchSortDirection.Descending
  };

  tableHeaders: DataTableHeaderOption[] = [
    { text: this.InvoicePeriodAndNumberText, key: Constants.InvoiceKey, width: '40%' },
    { text: this.BillGroupText, key: Constants.BillGroupKey, width: '45%' },
    { text: this.PaymentAmountText, key: this.AmountUiKey, align: 'right' }
  ];

  totalAmount = 0;

  // Data members
  private billingsSubject$ = new BehaviorSubject<PaymentData[]>([]);
  public billings$ = this.billingsSubject$.asObservable();

  // :  Must remain 'public' for unit testing
  createEndpointPaymentRequest(
    formGroup: UntypedFormGroup,
    groupNumber: string,
    paymentDataArray: PaymentData[]
  ): PaymentRequest {
    

    

    const lineItems: LineItem[] = paymentDataArray.map((paymentData) => {
      const invoiceInfo : InvoiceDetails = {
        period: paymentData.InvoicePeriod,
        number: paymentData.InvoiceNumber,
        amount: paymentData.Amount
      };

      const lineItem: LineItem = {
        invoice: invoiceInfo,
        billingGroupName: paymentData.BillGroupName,
        billingGroupNumber: paymentData.BillGroupNumber
      };

      return lineItem;
    });

    const paymentInformation = this.paymentInformationGet(formGroup);

    const paymentRequest : PaymentRequest = {
      currencyCode: Constants.CurrencyUsdKey,
      totalAmount: this.totalAmount,
      groupName: this.policyContext?.policyHolder,
      groupNumber: groupNumber,
      acctType: paymentInformation.accountType,
      acctNumber: paymentInformation.accountNumber,
      routingNumber: paymentInformation.routingNumber,
      lineItems: lineItems
    };

    return paymentRequest;
  }

  goBillingList() {
    this.parent.goBillingList();
  }

  modalClose(type: ModalType) {
    if (type === ModalType.Success || type === ModalType.Permission) {
      this.modalShow = false;
      this.parent.goBillingList();
    } else {
      this.modalShow = false;
      this.modalDataClear();
    }
  }

  modalOpen(type: ModalType) {
    this.modalShow = true;

    switch (type) {
      case ModalType.Error4xx:
        this.modalData.title = this.ModalTitleError4xx;
        this.modalData.body = this.ModalBodyError4xx;
        break;
      case ModalType.Error5xx:
        this.modalData.title = this.ModalTitleError5xx;
        this.modalData.body = this.ModalBodyError5xx;
        break;
      case ModalType.Legal:
        this.modalData.title = this.ModalTitleLegal;
        this.modalData.body = this.ModalBodyLegal;
        break;
      case ModalType.Permission:
        this.modalData.title = this.ModalTitlePermission;
        this.modalData.body = this.ModalBodyPermission;
        break;
      case ModalType.Success:
        this.modalData.title = this.ModalTitleSuccess;
        this.modalData.body = this.ModalBodySuccess;
        this.hasSubText = true;
        this.modalData.subText = this.ModalBodyEmail;
        break;

      default:
        break;
    }

    this.modalData.type = type;
    this.isLoading = false;
  }

  ngOnInit(): void {
    this.formGroup.get(this.AccountTypeSelectionKey)?.setValue(AccountType.Checking);
    this.hasSubmitPermission = this.hasPermissions(this.userContext);
    const paymentsInfos = this.billingService.getPaymentTableCells();
    if (paymentsInfos.length === 0) {
      this.goBillingList();
    }
    this.billingsSubject$.next(paymentsInfos);
    // Fetch data and update the BehaviorSubject
    combineLatest([this.page$, this.pageSize$, this.sortDirection$, this.sortField$])
      .pipe(
        switchMap(([page, pageSize, sortDirection, sortField]) => {
          this.searchParameter.page = page;
          this.searchParameter.pageSize = pageSize;
          this.searchParameter.sortDirection = sortDirection;
          this.searchParameter.sortField = sortField;
          return this.billingService.getPaymentLines(this.searchParameter);
        }),
        tap((data: PaymentData[]) => {
          this.billingsSubject$.next(data);
        }),
        catchError((error) => {
          console.error('Error fetching billing data:', error);
          this.billingsSubject$.next([]);
          return of([]);
        })
      )
      .subscribe();
  }

  onAmountChange(item: PaymentData) {
    let amountUiError = false;
    const currentBillings = this.billingsSubject$.getValue();
    const index = currentBillings.findIndex((billing: PaymentData) => billing.InvoiceNumber === item.InvoiceNumber);
    if (index !== -1) {
      currentBillings[index].AmountUi = this.amountUpdate(item).AmountUi;
      currentBillings[index].Amount = this.amountUpdate(item).Amount;
      amountUiError = !this.paymentFieldValid(currentBillings[index]);
      currentBillings[index].AmountUiError = amountUiError;
      this.billingsSubject$.next(currentBillings);
    }

    if (!amountUiError) {
      this.totalAmount = this.totalAmountCalculate(currentBillings);
    }
  }

  onClickTableHeader(sortField: string) {
    let newSortDirection = SearchSortDirection.Descending;

    if (this.searchParameter.sortDirection === SearchSortDirection.Descending) {
      newSortDirection = SearchSortDirection.Ascending;
    }

    const field: SearchSortField = SearchSortField[sortField as keyof typeof SearchSortField];
    this.sortDirection$.next(newSortDirection);
    this.sortField$.next(field);
  }

  pageValid() {
    const paymentInformation = this.paymentInformationGet(this.formGroup);
    const pageValid = this.pageValidLocal(this.paymentDataArray, paymentInformation);
    return pageValid;
  }

  payNow() {
    this.modalShow = true;
    this.isLoading = true;

    if (this.hasSubmitPermission) {
      if (this.formGroup.invalid) {
        this.controlsUpdate(this.formGroup);
        return;
      }
      
      const groupNumber = this.policyContext?.groupNumber ?? Constants.StringEmpty;

      this.paymentRequestSend(groupNumber, this.billingsSubject$.getValue());
    } else {
      this.modalOpen(ModalType.Permission);
    }
  }

  async submit() {
    console.log('Submitted');
  }

  private amountUpdate(item: PaymentData): PaymentData {
    const amountString = currencyStringCreate(item.AmountUi);
    const formattedAmount = currencyParse(amountString);
    const invoiceNumber = item.InvoiceNumber;
    return {
      ...item,
      Amount: formattedAmount,
      AmountUi: amountString,
      InvoiceNumber: invoiceNumber
    };
  }

  private controlsUpdate(control: AbstractControl) {
    if (control instanceof UntypedFormControl) {
      control.updateValueAndValidity();
    } else if (control instanceof UntypedFormGroup) {
      Object.values(control.controls).forEach((childControl) => this.controlsUpdate(childControl));
    } else if (control instanceof UntypedFormArray) {
      control.updateValueAndValidity();
      control.controls.forEach((childControl) => this.controlsUpdate(childControl));
    }
  }

  

  private hasPermissions(input: UserContext) {
    const formattedSymClass = input?.symClass?.toLowerCase();
    const isPermittedUsers = (formattedSymClass === 'sponsor' && input.policyholderInfo?.isAdmin === true) || formattedSymClass === 'distributor';
    if (isPermittedUsers) {
      return true;
    } else {
      return false;
    }
  }

  private modalDataClear() {
    this.modalData = {
      title: Constants.StringEmpty,
      body: Constants.StringEmpty,
      subText: Constants.StringEmpty,
      type: ModalType.Success
    };
  }

  private pageValidLocal(paymentDataArray: PaymentData[], paymentInformation: PaymentInformation): boolean {
    const paymentDataValidArray = paymentDataArray.map((paymentData) => {
      const paymentFieldValid = this.paymentFieldValid(paymentData);
      return paymentFieldValid;
    });

    const paymentInfoValid = this.paymentInformationValid(paymentInformation);

    const pageValid = !paymentDataValidArray.includes(false) && paymentInfoValid;

    return pageValid;
  }

  private paymentFieldValid(item: PaymentData): boolean {
    const fieldValue = item.AmountUi;
    const decimalPointCount = fieldValue.split('.').length - 1;
    const dollarSignCount = fieldValue.split('$').length - 1;
    const valueParsed = currencyParse(fieldValue);

    const fieldValid = decimalPointCount <= 1 && dollarSignCount <= 1 && valueParsed > 0;

    return fieldValid;
  }

  private paymentInformationGet(formGroup: UntypedFormGroup): PaymentInformation {
    const paymentInformation: PaymentInformation = {
      accountType: formGroup.get(this.AccountTypeSelectionKey)?.value,
      accountNumber: formGroup.get(this.AccountNumberKey)?.value,
      routingNumber: formGroup.get(this.RoutingNumberKey)?.value
    };

    return paymentInformation;
  }

  private paymentInformationValid(paymentInformation: PaymentInformation): boolean {
    const accountTypeValid = paymentInformation.accountType !== Constants.StringEmpty;

    const accountNumberValid =
      paymentInformation.accountNumber !== null &&
      paymentInformation.accountNumber !== undefined &&
      paymentInformation.accountNumber.length >= AccountFieldLengthMin;

    const routingNumberValid =
      paymentInformation.routingNumber !== null &&
      paymentInformation.routingNumber !== undefined &&
      paymentInformation.routingNumber.length == RoutingFieldLength;

    return accountTypeValid && accountNumberValid && routingNumberValid;
  }

  private paymentRequestSend(groupNumber: string, paymentDataArray: PaymentData[]) {
    const paymentRequest = this.createEndpointPaymentRequest(this.formGroup, groupNumber, paymentDataArray);

    // :  Prevent the centralized error handling in 'http-error.interceptor.ts' from redirecting to the "error page"
    //    (or to the dashboard if a centralized error page is not defined)
    this.errorService.preventNextErrorRedirect();

    this.paymentClient
      .bank(paymentRequest)
      .pipe(
        catchError((httpErrorResponse: HttpErrorResponse) => {
          return throwError(() => httpErrorResponse);
        })
      )
      .subscribe({
        complete: () => {
          // :  Success condition
          this.modalOpen(ModalType.Success);
        },
        error: (httpErrorResponse: HttpErrorResponse) => {
          // :  Error condition of some sort
          if (httpErrorResponse.status >= 500) {
            this.modalOpen(ModalType.Error5xx);
          } else {
            this.modalOpen(ModalType.Error4xx);
          }
        }
      });
  }

  private totalAmountCalculate(paymentDataArray: PaymentData[]): number {
    const total: number = paymentDataArray.reduce((accum, current) => {
      const amount = Number(current.Amount);
      const accumNew = accum + amount;
      return accumNew;
    }, 0.0);

    return total;
  }
}
